import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiSun, FiMoon, FiCheck, FiZap, FiServer, FiUser, FiShield, FiArrowRight, FiSettings, FiUsers, FiLink } from 'react-icons/fi';
import { useTheme, ThemeMode } from '../../context/ThemeContext';
import { ROUTES } from '../../app/routes';
import { isCommandCenterEnabled } from '../../utils/featureAccess';
import '../../styles/Settings.css';
const SettingsPage: React.FC = () => {
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const navCards = [
        ...(isCommandCenterEnabled() ? [{
                icon: <FiZap size={20}/>,
                accent: '#f59e0b',
                bg: 'rgba(245,158,11,0.12)',
                label: 'Connected Apps',
                desc: 'Connect Gmail, Calendar, Slack, and more for AI automation',
                route: ROUTES.SETTINGS_INTEGRATIONS,
            }] : []),
        {
            icon: <FiServer size={20}/>,
            accent: '#22d3ee',
            bg: 'rgba(34,211,238,0.12)',
            label: 'MCP Servers',
            desc: 'Connect and manage your Model Context Protocol servers',
            route: ROUTES.SETTINGS_MCP,
        },
        {
            icon: <FiLink size={20}/>,
            accent: '#f43f5e',
            bg: 'rgba(244,63,94,0.12)',
            label: 'Webhooks',
            desc: 'Get a secret key to create commands or trigger routines from outside Momentum AMP',
            route: ROUTES.SETTINGS_WEBHOOKS,
        },
        {
            icon: <FiUsers size={20}/>,
            accent: '#a200ff',
            bg: 'rgba(162,0,255,0.12)',
            label: 'Digital Workforce',
            desc: 'Choose the agent harness type that powers your Digital Workforce',
            route: ROUTES.SETTINGS_HARNESS_TYPE,
        },
        {
            icon: <FiUser size={20}/>,
            accent: '#818cf8',
            bg: 'rgba(129,140,248,0.12)',
            label: 'Profile Settings',
            desc: 'Manage your account name, avatar, and preferences',
            route: ROUTES.PROFILE,
        },
        {
            icon: <FiShield size={20}/>,
            accent: '#34d399',
            bg: 'rgba(52,211,153,0.12)',
            label: 'Security',
            desc: 'Update your password and security settings',
            route: ROUTES.PROFILE,
        },
    ];
    const cardVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.3 } }),
    };
    return (<div className="s2-page">
      <motion.div className="s2-container" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>

        <section className="s2-top-header">
          <div className="s2-top-header__text">
            <h1 className="s2-top-header__title">Settings</h1>
            <p className="s2-top-header__subtitle">Manage appearance and open key configuration panels for your workspace.</p>
          </div>
          <div className="s2-top-header__actions">
            <button className="s2-top-header__nav-btn" onClick={() => navigate(ROUTES.PROFILE)}>
              <FiUser size={14}/>
              <span>Profile</span>
            </button>
          
          </div>
        </section>

        
        <motion.section className="s2-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="s2-section-label">
            <span className="s2-section-dot s2-section-dot--purple"/>
            Appearance
          </div>

          <div className="s2-appearance-card">
            <div className="s2-appearance-left">
              <div className="s2-appearance-icon-wrap">
                {theme === 'dark' ? <FiMoon size={20}/> : <FiSun size={20}/>}
              </div>
              <div>
                <div className="s2-appearance-title">Theme</div>
                <div className="s2-appearance-sub">Choose your preferred visual mode</div>
              </div>
            </div>
            <div className="s2-theme-toggle">
              {(['light', 'dark'] as ThemeMode[]).map(t => (<button key={t} className={`s2-theme-btn${theme === t ? ' s2-theme-btn--active' : ''}`} onClick={() => setTheme(t)}>
                  {t === 'light' ? <FiSun size={14}/> : <FiMoon size={14}/>}
                  <span>{t === 'light' ? 'Light' : 'Dark'}</span>
                  {theme === t && <FiCheck size={12} className="s2-theme-check"/>}
                </button>))}
            </div>
          </div>
        </motion.section>

        
        <div className="s2-section-label s2-section-label--tight">
          <span className="s2-section-dot s2-section-dot--cyan"/>
          Quick Access
        </div>

        <div className="s2-grid">
          {navCards.map((card, i) => (<motion.div key={card.label} className="s2-nav-card" custom={i} variants={cardVariants} initial="hidden" animate="visible" whileHover={{ y: -3, boxShadow: `0 12px 40px rgba(0,0,0,0.25)` }} style={{ '--card-accent': card.accent } as React.CSSProperties} onClick={() => navigate(card.route)}>
              <div className="s2-nav-card__icon" style={{ background: card.bg, color: card.accent }}>
                {card.icon}
              </div>
              <div className="s2-nav-card__body">
                <div className="s2-nav-card__label">{card.label}</div>
                <div className="s2-nav-card__desc">{card.desc}</div>
              </div>
              <div className="s2-nav-card__meta">
                <span className="s2-nav-card__open">Open</span>
                <div className="s2-nav-card__arrow" style={{ color: card.accent }}>
                  <FiArrowRight size={16}/>
                </div>
              </div>
            </motion.div>))}
        </div>

      </motion.div>
    </div>);
};
export default SettingsPage;
